import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { BORDERLESS_SELECT_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";

/** The tri-state's own values. Not persisted — they map onto `isUnion` (null / true / false). */
const UNION_SCOPE = { all: "all", union: "union", nonUnion: "non_union" } as const;
type UnionScope = (typeof UNION_SCOPE)[keyof typeof UNION_SCOPE];

/** The patch a table applies to the row. Mirrors `{ isUnion, union }` on the config models. */
export type UnionCellPatch = { isUnion?: boolean | null; union?: string[] };

type Props = {
	isUnion: boolean | null | undefined;
	union: string[] | undefined;
	/** The department's known union names, from `useGetDepartmentNurseUnions()`. */
	existingUnions: string[] | undefined;
	onChange: (patch: UnionCellPatch) => void;
	/** Accessible name — these cells have no visible label. */
	ariaLabel: string;
	dataTestId: string;
	/** Read-only access: the scope stays readable but cannot be changed. */
	disabled: boolean;
};

/**
 * Borderless union-scope cell shared by the dense config tables (Break Rules, Overtime, Hours Rate).
 *
 * Extracted rather than copied a third time: the three tables must agree on exactly what each state
 * writes — in particular that switching to "All" or "Non-union" also clears `union`, since a leftover
 * name would keep narrowing a filter the UI no longer shows.
 */
export const ConfigUnionCell = ({ isUnion, union, existingUnions, onChange, ariaLabel, dataTestId, disabled }: Props) => {
	const { t } = useTranslation();
	const unionCount = existingUnions?.length ?? 0;
	const scope: UnionScope = isUnion === true ? UNION_SCOPE.union : isUnion === false ? UNION_SCOPE.nonUnion : UNION_SCOPE.all;

	return (
		<>
			<SystemDropdown<UnionScope>
				fullWidth
				selectSx={BORDERLESS_SELECT_SX}
				aria-label={ariaLabel}
				data-testid={dataTestId}
				items={[
					{ value: UNION_SCOPE.all, label: t("department.config.rates.option.all") },
					{ value: UNION_SCOPE.union, label: t("department.config.rates.union.membersOnly") },
					{ value: UNION_SCOPE.nonUnion, label: t("department.config.rates.union.nonUnionOnly") },
				]}
				selectedItem={scope}
				disabled={disabled}
				onChange={(value) => {
					if (value === undefined) return;
					if (value === UNION_SCOPE.all) onChange({ isUnion: null, union: [] });
					else if (value === UNION_SCOPE.nonUnion) onChange({ isUnion: false, union: [] });
					else onChange({ isUnion: true });
				}}
			/>
			{isUnion === true && (
				<SystemDropdown<string>
					fullWidth
					multiple
					selectSx={BORDERLESS_SELECT_SX}
					label={t("department.config.rates.union.specific")}
					disabled={disabled || unionCount === 0}
					items={(existingUnions ?? []).map((u) => ({ value: u, label: u, selectedLabel: u }))}
					selectedItems={union}
					valueToString={(v) => String(v)}
					allOption={unionCount > 0 ? t("department.config.rates.union.all") : ""}
					data-testid={`${dataTestId}-specific`}
					onChangeMultiple={(selectedValues) => {
						const unions = selectedValues.filter((v): v is string => typeof v === "string" && v !== "__all__");
						onChange({ union: unions });
					}}
					MenuProps={{
						sx: { "& .MuiMenuItem-root": { textTransform: "none" } },
					}}
				/>
			)}
		</>
	);
};
